import { z } from "zod";
// Reuse the canonical Json type + schema from the shared barrel's first module
// so `export *` from index.ts has exactly one `Json` / `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Contracts shared contract (Strangler-Fig migration, manifest-6).
 *
 * Single source of truth for the `contracts` wire shape shared by the web SPA
 * (`src/repositories/contracts.ts`) and the NestJS slice (`apps/api/src/contracts/*`).
 * Shape is **snake_case** so the frontend<->Nest transport is a field-for-field
 * swap (no renames), matching the Supabase generated `contracts` Row and the
 * Nest repository `.select()` lists.
 *
 * The web repo `.parse()`s Nest responses against {@link contractSchema} at the
 * flip boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): the staff list/detail
 * queries return the contract row JOINED with nested `property` / `unit` /
 * `tenant` / `owner` relations. Those nested objects vary in column set between
 * the list select and the detail select, and may be `null` (FK nullable). So the
 * relation fields are `.optional()` + `.nullable()` with a **passthrough** object
 * shape — a too-strict relation schema would make every Nest call silently fall
 * back to Supabase. Only the contract's own columns are pinned tightly.
 *
 * BE adoption note: `apps/api` does not yet import this package — it keeps its
 * class-validator DTO (already shape-aligned). Wiring the Nest side to consume
 * `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** Contract lifecycle statuses (advisory — the row keeps `status` an open string to never reject server data). */
export const CONTRACT_STATUSES = [
  "draft",
  "active",
  "expired",
  "terminated",
  "cancelled",
] as const;
export const contractStatusSchema = z.enum(CONTRACT_STATUSES);
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** Contract kinds (advisory — `contract_type` stays an open string). */
export const CONTRACT_TYPES = ["rent", "sale", "management", "brokerage"] as const;
export const contractTypeSchema = z.enum(CONTRACT_TYPES);
export type ContractType = (typeof CONTRACT_TYPES)[number];

/**
 * Loose, passthrough relation shape. The staff list/detail selects embed
 * different column subsets of property/unit/tenant/owner; keep this permissive
 * so the parser never rejects a valid joined payload (#1699 lesson). `null`
 * because the FKs (tenant_id, owner_id) are nullable.
 */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A persisted contract row as the staff dashboard consumes it. The own-columns
 * field set + nullability are byte-identical to the Supabase generated
 * `contracts` Row, so it can be the single source of truth without changing any
 * consumer. The four nested relations are appended by the list/detail joins.
 */
export const contractSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    contract_number: z.string().nullable(),
    contract_type: z.string(),
    status: z.string(),
    property_id: z.string(),
    unit_id: z.string(),
    tenant_id: z.string().nullable(),
    owner_id: z.string().nullable(),
    start_date: z.string(),
    end_date: z.string(),
    rent_amount: z.number().nullable(),
    total_amount: z.number().nullable(),
    deposit_amount: z.number().nullable(),
    payment_frequency: z.string().nullable(),
    payments_count: z.number().nullable(),
    ejar_number: z.string().nullable(),
    ejar_document_url: z.string().nullable(),
    ejar_verified: z.boolean(),
    ejar_verified_at: z.string().nullable(),
    is_sublease: z.boolean(),
    parent_contract_id: z.string().nullable(),
    parent_ejar_contract_number: z.string().nullable(),
    notes: z.string().nullable(),
    terms: z.string().nullable(),
    documents: jsonSchema.nullable(),
    contract_fee: z.number().nullable(),
    contract_fee_payer: z.string().nullable(),
    management_fee: z.number().nullable(),
    management_fee_payer: z.string().nullable(),
    brokerage_fee: z.number().nullable(),
    brokerage_fee_payer: z.string().nullable(),
    marketing_fee: z.number().nullable(),
    marketing_fee_payer: z.string().nullable(),
    contract_management_fee: z.number().nullable(),
    contract_management_fee_payer: z.string().nullable(),
    property_management_fee: z.number().nullable(),
    property_management_fee_payer: z.string().nullable(),
    deleted_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    // Joined relations (list + detail) — loose + optional + nullable.
    property: relation,
    unit: relation,
    tenant: relation,
    owner: relation,
  })
  // Tolerate extra columns the server may add (new migration columns) without
  // forcing a same-PR schema bump — keeps the flip from silently falling back.
  .passthrough();

export type Contract = z.infer<typeof contractSchema>;

/**
 * Minimal stats projection the staff dashboard counts in-memory
 * (status + contract_type + end_date). Mirrors `contractsRepository.stats`.
 */
export const contractStatsRowSchema = z
  .object({
    status: z.string(),
    contract_type: z.string(),
    end_date: z.string(),
  })
  .passthrough();

export type ContractStatsRow = z.infer<typeof contractStatsRowSchema>;

/**
 * `GET /contracts/next-number` envelope (wraps the `generate_contract_number`
 * RPC). Mirrors `contractsRepository.generateContractNumber`.
 */
export const nextContractNumberResponseSchema = z.object({
  contract_number: z.string().nullable(),
});

export type NextContractNumberResponse = z.infer<typeof nextContractNumberResponseSchema>;

/**
 * `DELETE /contracts/:id` response (wraps the `delete_contract_cascade` RPC,
 * which returns the `get_contract_delete_summary` jsonb count object —
 * `supabase/baseline/public_schema.sql`). `.passthrough()` per the tolerance
 * rule above: the RPC may grow more counted tables without a same-PR bump.
 */
export const contractDeleteSummarySchema = z
  .object({
    invoices: z.number(),
    receipts: z.number(),
    expenses: z.number(),
    scheduled_payments: z.number(),
  })
  .passthrough();

export type ContractDeleteSummary = z.infer<typeof contractDeleteSummarySchema>;
